import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Calendar as CalendarIcon,
    RefreshCw,
    Send,
    Trash2,
    X,
    Filter,
    Play,
    DollarSign,
    Edit2,
    ExternalLink,
    Megaphone,
    FileText,
    Clock,
    CheckCircle,
    AlertCircle,
    XCircle
} from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { CalendarGrid, CoverageBar, CreativeDock, GapSuggestions } from '../components/scheduler';
import ConfirmationModal from '../components/ConfirmationModal';
import { calculateCoverage } from '../utils/schedulerAI';
import { loadLibraryItems } from '../services/libraryService';
import { getPillarsByBrand } from '../services/pillarService';
import PillarSelector from '../components/strategy/PillarSelector';
import EditScheduledPostModal from '../components/content-scheduler/EditScheduledPostModal';
import EditCampaignModal from '../components/content-scheduler/EditCampaignModal';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';

const API_BASE = 'http://localhost:3001';

const ContentScheduler = () => {
    const navigate = useNavigate();
    const {
        scheduledPosts,
        creatives,
        schedulePost,
        unschedulePost,
        selectedBrand
    } = useApp();
    const { currentUser } = useAuth();

    // Library items state (includes carousels)
    const [libraryItems, setLibraryItems] = useState([]);
    const [libraryLoading, setLibraryLoading] = useState(false);

    // Marketing pillar filter state
    const [availablePillars, setAvailablePillars] = useState([]);
    const [selectedPillarFilter, setSelectedPillarFilter] = useState(null); // null = show all

    // Content type filter: 'all', 'organic', 'paid'
    const [contentTypeFilter, setContentTypeFilter] = useState('all');

    // Post status filter: 'all', 'scheduled', 'published', 'failed', 'cancelled'
    const [statusFilter, setStatusFilter] = useState('all');

    // Paid campaigns state
    const [paidCampaigns, setPaidCampaigns] = useState([]);
    const [campaignsLoading, setCampaignsLoading] = useState(false);

    // Edit modals state
    const [editPostModalOpen, setEditPostModalOpen] = useState(false);
    const [editCampaignModalOpen, setEditCampaignModalOpen] = useState(false);
    const [selectedPostForEdit, setSelectedPostForEdit] = useState(null);
    const [selectedCampaignForEdit, setSelectedCampaignForEdit] = useState(null);

    // Load library items (carousels and images) when brand changes
    const loadLibraryCreatives = useCallback(async () => {
        if (!currentUser?.uid || !selectedBrand?.id) {
            setLibraryItems([]);
            return;
        }

        setLibraryLoading(true);
        try {
            const items = await loadLibraryItems(currentUser.uid, selectedBrand.id, {});
            // Filter to only include creative types (carousel and image)
            const creativeItems = items.filter(item =>
                item.type === 'carousel' || item.type === 'image'
            );
            setLibraryItems(creativeItems);
        } catch (error) {
            console.error('Failed to load library items:', error);
            setLibraryItems([]);
        } finally {
            setLibraryLoading(false);
        }
    }, [currentUser?.uid, selectedBrand?.id]);

    useEffect(() => {
        loadLibraryCreatives();
    }, [loadLibraryCreatives]);

    // Load available pillars when brand changes
    useEffect(() => {
        const loadPillars = async () => {
            if (!selectedBrand?.id) {
                setAvailablePillars([]);
                setSelectedPillarFilter(null);
                return;
            }

            try {
                const pillars = await getPillarsByBrand(selectedBrand.id);
                setAvailablePillars(pillars);
            } catch (error) {
                console.error('Error loading pillars:', error);
                setAvailablePillars([]);
            }
        };

        loadPillars();
    }, [selectedBrand?.id]);

    // Load paid campaigns from Firestore
    const loadPaidCampaigns = useCallback(async () => {
        if (!currentUser?.uid || !selectedBrand?.id) {
            setPaidCampaigns([]);
            return;
        }

        setCampaignsLoading(true);
        try {
            // Query campaigns collection for this brand
            const campaignsRef = collection(db, 'campaigns');
            const q = query(
                campaignsRef,
                where('brandId', '==', selectedBrand.id),
                where('userId', '==', currentUser.uid),
                orderBy('createdAt', 'desc')
            );

            const snapshot = await getDocs(q);
            const campaigns = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                type: 'paid' // Mark as paid for filtering
            }));

            setPaidCampaigns(campaigns);
        } catch (error) {
            console.error('Failed to load paid campaigns:', error);
            setPaidCampaigns([]);
        } finally {
            setCampaignsLoading(false);
        }
    }, [currentUser?.uid, selectedBrand?.id]);

    useEffect(() => {
        loadPaidCampaigns();
    }, [loadPaidCampaigns]);

    // Filter and combine data by selected brand
    const brandCreatives = useMemo(() => {
        if (!selectedBrand) return [];

        // Get creatives from context (legacy)
        const contextCreatives = creatives
            .filter(c => c.brandId === selectedBrand.id)
            .map(c => ({
                ...c,
                // Normalize fields for consistency
                imageUrl: c.imageUrl || c.url,
                title: c.title || 'Untitled',
                source: 'context'
            }));

        // Get library items (includes carousels)
        const libraryCreatives = libraryItems.map(item => ({
            ...item,
            // Normalize fields for scheduler compatibility
            title: item.title || item.name || 'Untitled',
            brandName: selectedBrand.name,
            brandId: selectedBrand.id,
            // For carousels, use first image as preview
            url: item.type === 'carousel' && item.carouselImages?.length > 0
                ? item.carouselImages[0].imageUrl
                : item.imageUrl,
            source: 'library'
        }));

        // Combine both sources, library items first (newer)
        return [...libraryCreatives, ...contextCreatives];
    }, [creatives, libraryItems, selectedBrand]);

    const brandScheduledPosts = useMemo(() => {
        if (!selectedBrand) return [];
        return scheduledPosts.filter(p => p.brandId === selectedBrand.id);
    }, [scheduledPosts, selectedBrand]);

    // Filter creatives by pillar if selected
    const filteredCreatives = useMemo(() => {
        if (!selectedPillarFilter) return brandCreatives;
        return brandCreatives.filter(c => c.pillarId === selectedPillarFilter);
    }, [brandCreatives, selectedPillarFilter]);

    // Filter scheduled posts by pillar and status
    const filteredScheduledPosts = useMemo(() => {
        let posts = brandScheduledPosts;

        // Filter by pillar
        if (selectedPillarFilter) {
            posts = posts.filter(p => p.pillarId === selectedPillarFilter);
        }

        // Filter by status
        if (statusFilter !== 'all') {
            posts = posts.filter(p => p.status === statusFilter);
        }

        return posts;
    }, [brandScheduledPosts, selectedPillarFilter, statusFilter]);

    // Count posts by status for filter tabs
    const statusCounts = useMemo(() => {
        const posts = selectedPillarFilter
            ? brandScheduledPosts.filter(p => p.pillarId === selectedPillarFilter)
            : brandScheduledPosts;

        return {
            all: posts.length,
            draft: posts.filter(p => p.status === 'draft').length,
            scheduled: posts.filter(p => p.status === 'scheduled' || !p.status).length,
            published: posts.filter(p => p.status === 'published').length,
            failed: posts.filter(p => p.status === 'failed').length,
            cancelled: posts.filter(p => p.status === 'cancelled').length
        };
    }, [brandScheduledPosts, selectedPillarFilter]);

    // Unified content: combine organic posts and paid campaigns
    const unifiedContent = useMemo(() => {
        // Mark organic posts
        const organicPosts = filteredScheduledPosts.map(p => ({
            ...p,
            contentType: 'organic',
            displayType: 'Organic Post'
        }));

        // Mark paid campaigns (filter by pillar if needed)
        let filteredCampaigns = paidCampaigns;
        if (selectedPillarFilter) {
            filteredCampaigns = paidCampaigns.filter(c =>
                c.pillarId === selectedPillarFilter ||
                c.sourceBriefIds?.some(id =>
                    filteredScheduledPosts.some(p => p.briefId === id)
                )
            );
        }

        const paidItems = filteredCampaigns.map(c => ({
            ...c,
            contentType: 'paid',
            displayType: 'Paid Campaign',
            // Normalize scheduling date for calendar display
            scheduledDate: c.startDate || c.createdAt
        }));

        // Combine both
        return [...organicPosts, ...paidItems];
    }, [filteredScheduledPosts, paidCampaigns, selectedPillarFilter]);

    // Apply content type filter
    const displayContent = useMemo(() => {
        if (contentTypeFilter === 'all') return unifiedContent;
        return unifiedContent.filter(item => item.contentType === contentTypeFilter);
    }, [unifiedContent, contentTypeFilter]);

    // Count by type for tabs
    const contentCounts = useMemo(() => ({
        all: unifiedContent.length,
        organic: unifiedContent.filter(c => c.contentType === 'organic').length,
        paid: unifiedContent.filter(c => c.contentType === 'paid').length
    }), [unifiedContent]);

    // Calendar state
    const [currentDate, setCurrentDate] = useState(new Date());

    // Meta sync state
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Modal states
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [showGapSuggestions, setShowGapSuggestions] = useState(false);
    const [showUnscheduleConfirm, setShowUnscheduleConfirm] = useState(false);

    // Selected items for modals
    const [selectedCreative, setSelectedCreative] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [creativeToUnschedule, setCreativeToUnschedule] = useState(null);

    // Schedule form data
    const [scheduleFormData, setScheduleFormData] = useState({
        date: '',
        time: '',
        platforms: [],
        timezone: 'EST',
        caption: '',
        hashtags: '',
        cta: '',
        productPageUrl: ''
    });

    const platformOptions = ['instagram', 'facebook', 'linkedin', 'tiktok', 'twitter', 'google'];

    // Calculate coverage stats
    const coverage = useMemo(() => {
        return calculateCoverage(brandScheduledPosts, 30);
    }, [brandScheduledPosts]);

    // Fetch scheduled content from Meta
    const fetchMetaScheduledContent = useCallback(async () => {
        if (!currentUser?.uid || !selectedBrand?.id) return;

        setIsLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                userId: currentUser.uid,
                brandId: selectedBrand.id
            });

            const response = await fetch(`${API_BASE}/api/campaigns/scheduled-content?${params}`);

            if (!response.ok) {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to fetch scheduled content');
            }
        } catch (err) {
            console.error('Error fetching Meta scheduled content:', err);
            setError('Failed to connect to server');
        } finally {
            setIsLoading(false);
        }
    }, [currentUser?.uid, selectedBrand?.id]);

    // Fetch on mount and when brand changes
    useEffect(() => {
        fetchMetaScheduledContent();
    }, [fetchMetaScheduledContent]);

    // Calendar navigation
    const nextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    };

    const prevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    };

    // Handle drop from dock to calendar
    // Helper to format date as YYYY-MM-DD using local timezone (not UTC)
    const formatLocalDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleDrop = (dragData, targetDate) => {
        const creative = brandCreatives.find(c => c.id === dragData.id);
        setSelectedCreative(creative);
        setSelectedDate(targetDate);

        // Pre-fill the form with the target date and creative's caption/hashtags
        // Use local date formatting to avoid timezone issues with toISOString()
        const dateStr = formatLocalDate(targetDate);
        setScheduleFormData(prev => ({
            ...prev,
            date: dateStr,
            time: '12:00',
            caption: creative?.caption || creative?.description || '',
            hashtags: creative?.hashtags || '',
            cta: creative?.cta || creative?.metadata?.cta || '',
            productPageUrl: creative?.productPageUrl || creative?.metadata?.productPageUrl || ''
        }));

        setShowScheduleModal(true);
    };

    // Handle gap click - show suggestions
    const handleGapClick = (date) => {
        setSelectedDate(date);
        setShowGapSuggestions(true);
    };

    // Handle scheduling from gap suggestions
    const handleScheduleFromSuggestion = (creative, date) => {
        setSelectedCreative(creative);
        setSelectedDate(date);
        setShowGapSuggestions(false);

        // Pre-fill the form with creative's caption/hashtags
        // Use local date formatting to avoid timezone issues
        const dateStr = formatLocalDate(date);
        setScheduleFormData(prev => ({
            ...prev,
            date: dateStr,
            time: '12:00',
            caption: creative?.caption || creative?.description || '',
            hashtags: creative?.hashtags || '',
            cta: creative?.cta || creative?.metadata?.cta || '',
            productPageUrl: creative?.productPageUrl || creative?.metadata?.productPageUrl || ''
        }));

        setShowScheduleModal(true);
    };

    // Handle creative click from calendar
    const handleCreativeClick = (creative, post) => {
        // Use the post data which contains the Firestore document id, scheduledDate, platforms, etc.
        // Merge creative data (imageUrl, etc.) with post data (id, scheduledDate, platforms)
        const postWithCreativeData = {
            ...post,
            ...creative,
            // Ensure we keep the post id (Firestore document id) not the creative id
            id: post?.id || creative?.id,
            creativeId: creative?.id,
            contentType: post?.contentType || 'organic'
        };

        // Check if this is a paid campaign or organic post
        if (postWithCreativeData.contentType === 'paid') {
            // Find the campaign from paidCampaigns
            const campaign = paidCampaigns.find(c => c.id === postWithCreativeData.id);
            if (campaign) {
                setSelectedCampaignForEdit(campaign);
                setEditCampaignModalOpen(true);
            }
        } else {
            // For organic posts, open the edit post modal directly
            setSelectedPostForEdit(postWithCreativeData);
            setEditPostModalOpen(true);
        }
    };

    // Legacy handler for unschedule modal (kept for backwards compatibility)
    const handleCreativeClickLegacy = (creative, post) => {
        const postWithCreativeData = {
            ...post,
            ...creative,
            id: post?.id || creative?.id,
            creativeId: creative?.id
        };
        setCreativeToUnschedule(postWithCreativeData);
        setShowUnscheduleConfirm(true);
    };

    // Submit schedule
    const handleSchedule = async (e) => {
        e.preventDefault();
        if (!selectedCreative || scheduleFormData.platforms.length === 0) return;

        try {
            const dateTime = new Date(`${scheduleFormData.date}T${scheduleFormData.time}`);
            // Include caption, hashtags, CTA, and productPageUrl from the form in the creative data
            const creativeWithPostData = {
                ...selectedCreative,
                caption: scheduleFormData.caption,
                hashtags: scheduleFormData.hashtags,
                cta: scheduleFormData.cta,
                productPageUrl: scheduleFormData.productPageUrl
            };
            // Pass creativeWithPostData as the 5th parameter to support library items
            await schedulePost(
                selectedCreative.id,
                scheduleFormData.platforms,
                dateTime.toISOString(),
                scheduleFormData.timezone,
                creativeWithPostData
            );

            setShowScheduleModal(false);
            setSelectedCreative(null);
            setSelectedDate(null);
            setScheduleFormData({ date: '', time: '', platforms: [], timezone: 'EST', caption: '', hashtags: '', cta: '', productPageUrl: '' });
        } catch (error) {
            console.error('Error scheduling post:', error);
        }
    };

    // Handle unschedule
    const handleUnschedule = async () => {
        if (!creativeToUnschedule) return;

        try {
            // Use creativeId for the unschedulePost function (it updates the creative document)
            await unschedulePost(creativeToUnschedule.creativeId || creativeToUnschedule.id);
            setShowUnscheduleConfirm(false);
            setCreativeToUnschedule(null);
            setPublishError(null);
        } catch (error) {
            console.error('Error unscheduling:', error);
        }
    };

    // Handle opening edit modal based on content type
    const handleEditContent = (item) => {
        if (item.contentType === 'paid') {
            setSelectedCampaignForEdit(item);
            setEditCampaignModalOpen(true);
        } else {
            setSelectedPostForEdit(item);
            setEditPostModalOpen(true);
        }
    };

    // Handle post update from edit modal
    const handlePostUpdate = (postId, updatedPost) => {
        // The AppContext should handle this via its own state management
        // For now we'll just close the modal and trigger a refresh
        setEditPostModalOpen(false);
        setSelectedPostForEdit(null);
    };

    // Handle unpublish from edit modal
    const handleUnpublishPost = (postId) => {
        // Post status changed to scheduled - refresh will pick it up
        setEditPostModalOpen(false);
        setSelectedPostForEdit(null);
    };

    // Handle republish from edit modal
    const handleRepublishPost = (postId) => {
        // Post rescheduled - refresh will pick it up
        setEditPostModalOpen(false);
        setSelectedPostForEdit(null);
    };

    // Handle campaign update from edit modal
    const handleCampaignUpdate = (campaignId, updatedCampaign) => {
        setPaidCampaigns(prev =>
            prev.map(c => c.id === campaignId ? { ...c, ...updatedCampaign } : c)
        );
        setEditCampaignModalOpen(false);
        setSelectedCampaignForEdit(null);
    };

    // Handle navigate to funnel builder
    const handleNavigateToFunnel = (funnelId) => {
        navigate(`/funnel-builder/${funnelId}`);
    };

    // Handle cancel post from edit modal
    const handleCancelPostFromModal = async (postId) => {
        try {
            await unschedulePost(postId);
            setEditPostModalOpen(false);
            setSelectedPostForEdit(null);
        } catch (error) {
            console.error('Error cancelling post:', error);
        }
    };

    // Handle publish now
    const [isPublishing, setIsPublishing] = useState(false);
    const [publishError, setPublishError] = useState(null);

    const handlePublishNow = async (post) => {
        if (!post || !currentUser) return;

        setIsPublishing(true);
        setPublishError(null);

        try {
            const response = await fetch(`${API_BASE}/api/scheduler/publish/${post.id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: currentUser.uid })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to publish');
            }

            // Close the modal and show success
            setShowUnscheduleConfirm(false);
            setCreativeToUnschedule(null);

        } catch (error) {
            console.error('Error publishing:', error);
            setPublishError(error.message);
        } finally {
            setIsPublishing(false);
        }
    };

    return (
        <div className="animate-fadeIn">
            {/* Header */}
            <div style={{
                marginBottom: 'var(--space-4)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
            }}>
                <div>
                    <h1>Content Scheduler</h1>
                    <p className="text-secondary">
                        {selectedBrand
                            ? `Schedule and manage posts for ${selectedBrand.name}`
                            : 'Select a brand to view scheduled content'}
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    {/* Pillar Filter */}
                    {selectedBrand && availablePillars.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <Filter size={16} style={{ color: 'var(--color-text-tertiary)' }} />
                            <PillarSelector
                                pillars={availablePillars}
                                value={selectedPillarFilter}
                                onChange={(pillarId) => setSelectedPillarFilter(pillarId)}
                                mode="compact"
                                placeholder="All pillars"
                                showClear={true}
                            />
                        </div>
                    )}
                    {selectedBrand && (
                        <button
                            className="btn btn-secondary"
                            onClick={() => {
                                fetchMetaScheduledContent();
                                loadPaidCampaigns();
                            }}
                            disabled={isLoading || campaignsLoading}
                        >
                            <RefreshCw size={16} className={(isLoading || campaignsLoading) ? 'animate-spin' : ''} />
                            {(isLoading || campaignsLoading) ? 'Syncing...' : 'Sync Content'}
                        </button>
                    )}
                </div>
            </div>

            {/* Content Type Filter Tabs */}
            {selectedBrand && (
                <div style={{
                    display: 'flex',
                    gap: 'var(--space-1)',
                    marginBottom: 'var(--space-4)',
                    borderBottom: '1px solid var(--color-border)',
                    paddingBottom: '1px'
                }}>
                    {[
                        { id: 'all', label: 'All Content', icon: CalendarIcon },
                        { id: 'organic', label: 'Organic', icon: FileText },
                        { id: 'paid', label: 'Paid Campaigns', icon: Megaphone }
                    ].map(tab => {
                        const Icon = tab.icon;
                        const count = contentCounts[tab.id];
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setContentTypeFilter(tab.id)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    padding: 'var(--space-2) var(--space-4)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    borderBottom: contentTypeFilter === tab.id
                                        ? '2px solid var(--color-primary)'
                                        : '2px solid transparent',
                                    color: contentTypeFilter === tab.id
                                        ? 'var(--color-primary)'
                                        : 'var(--color-text-secondary)',
                                    fontWeight: contentTypeFilter === tab.id ? 500 : 400,
                                    marginBottom: '-2px',
                                    transition: 'all 0.15s ease'
                                }}
                            >
                                <Icon size={16} />
                                {tab.label}
                                <span style={{
                                    background: contentTypeFilter === tab.id
                                        ? 'var(--color-primary)'
                                        : 'var(--color-bg-tertiary)',
                                    color: contentTypeFilter === tab.id
                                        ? 'white'
                                        : 'var(--color-text-secondary)',
                                    padding: '2px 8px',
                                    borderRadius: 'var(--radius-full)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 500
                                }}>
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Status Filter Pills - show when viewing organic content */}
            {selectedBrand && contentTypeFilter !== 'paid' && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    marginBottom: 'var(--space-4)'
                }}>
                    <span style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        Status:
                    </span>
                    {[
                        { id: 'all', label: 'All', icon: null, color: 'var(--color-text-secondary)' },
                        { id: 'draft', label: 'Draft', icon: FileText, color: 'var(--color-text-tertiary)' },
                        { id: 'scheduled', label: 'Scheduled', icon: Clock, color: 'var(--color-primary)' },
                        { id: 'published', label: 'Published', icon: CheckCircle, color: 'var(--success-500)' },
                        { id: 'failed', label: 'Failed', icon: AlertCircle, color: 'var(--error-500)' }
                    ].map(status => {
                        const count = statusCounts[status.id];
                        const isSelected = statusFilter === status.id;
                        // Skip showing filter if count is 0 (except 'all')
                        if (status.id !== 'all' && count === 0) return null;

                        return (
                            <button
                                key={status.id}
                                onClick={() => setStatusFilter(status.id)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '4px 12px',
                                    background: isSelected ? `${status.color}15` : 'var(--color-bg-secondary)',
                                    border: isSelected ? `1px solid ${status.color}` : '1px solid var(--color-border)',
                                    borderRadius: 'var(--radius-full)',
                                    cursor: 'pointer',
                                    fontSize: 'var(--font-size-sm)',
                                    color: isSelected ? status.color : 'var(--color-text-secondary)',
                                    fontWeight: isSelected ? 500 : 400,
                                    transition: 'all 0.15s ease'
                                }}
                            >
                                {status.icon && React.createElement(status.icon, { size: 14 })}
                                {status.label}
                                <span style={{
                                    background: isSelected ? status.color : 'var(--color-bg-tertiary)',
                                    color: isSelected ? 'white' : 'var(--color-text-tertiary)',
                                    padding: '1px 6px',
                                    borderRadius: 'var(--radius-full)',
                                    fontSize: '10px',
                                    fontWeight: 600,
                                    marginLeft: '2px'
                                }}>
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Error message */}
            {error && (
                <div
                    className="card"
                    style={{
                        marginBottom: 'var(--space-4)',
                        background: 'var(--color-error-bg)',
                        border: '1px solid var(--color-error)'
                    }}
                >
                    <p style={{ color: 'var(--color-error)', margin: 0 }}>{error}</p>
                </div>
            )}

            {/* Coverage bar */}
            {selectedBrand && (
                <div style={{ marginBottom: 'var(--space-4)' }}>
                    <CoverageBar
                        percentage={coverage.percentage}
                        covered={coverage.covered}
                        total={coverage.total}
                        gaps={coverage.total - coverage.covered}
                    />
                </div>
            )}

            {/* Main layout: Calendar + Dock */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: selectedBrand ? '1fr 320px' : '1fr',
                gap: 'var(--space-4)',
                alignItems: 'start'
            }}>
                {/* Calendar Grid */}
                <CalendarGrid
                    currentDate={currentDate}
                    onPrevMonth={prevMonth}
                    onNextMonth={nextMonth}
                    scheduledPosts={filteredScheduledPosts}
                    creatives={filteredCreatives}
                    onDrop={handleDrop}
                    onCreativeClick={handleCreativeClick}
                    onGapClick={handleGapClick}
                />

                {/* Creative Dock */}
                {selectedBrand && (
                    <div style={{ height: 'calc(100vh - 280px)', minHeight: '500px' }}>
                        <CreativeDock
                            creatives={filteredCreatives}
                            scheduledPosts={filteredScheduledPosts}
                            selectedPillar={availablePillars.find(p => p.id === selectedPillarFilter)}
                        />
                    </div>
                )}
            </div>

            {/* Paid Campaigns List - shows when paid tab selected and has campaigns */}
            {selectedBrand && contentTypeFilter === 'paid' && paidCampaigns.length > 0 && (
                <div style={{ marginTop: 'var(--space-4)' }}>
                    <h3 style={{ marginBottom: 'var(--space-3)' }}>Active Campaigns</h3>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                        gap: 'var(--space-3)'
                    }}>
                        {paidCampaigns.map(campaign => (
                            <div
                                key={campaign.id}
                                className="card"
                                style={{
                                    padding: 'var(--space-3)',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    border: '1px solid var(--color-border)'
                                }}
                                onClick={() => {
                                    setSelectedCampaignForEdit(campaign);
                                    setEditCampaignModalOpen(true);
                                }}
                            >
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 'var(--space-3)'
                                }}>
                                    {/* Campaign thumbnail */}
                                    {(campaign.imageUrl || campaign.creative?.imageUrl) ? (
                                        <img
                                            src={campaign.imageUrl || campaign.creative?.imageUrl}
                                            alt={campaign.name}
                                            style={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: 'var(--radius-md)',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    ) : (
                                        <div style={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: 'var(--radius-md)',
                                            background: 'var(--color-bg-tertiary)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <Megaphone size={24} style={{ color: 'var(--color-text-tertiary)' }} />
                                        </div>
                                    )}

                                    {/* Campaign info */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            marginBottom: 'var(--space-1)'
                                        }}>
                                            <h4 style={{
                                                margin: 0,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {campaign.name}
                                            </h4>
                                            <span style={{
                                                padding: '2px 8px',
                                                borderRadius: 'var(--radius-full)',
                                                fontSize: 'var(--font-size-xs)',
                                                fontWeight: 500,
                                                background: campaign.status === 'ACTIVE'
                                                    ? 'var(--success-100)'
                                                    : campaign.status === 'PAUSED'
                                                        ? 'var(--warning-100)'
                                                        : 'var(--color-bg-tertiary)',
                                                color: campaign.status === 'ACTIVE'
                                                    ? 'var(--success-700)'
                                                    : campaign.status === 'PAUSED'
                                                        ? 'var(--warning-700)'
                                                        : 'var(--color-text-secondary)'
                                            }}>
                                                {campaign.status}
                                            </span>
                                        </div>

                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-3)',
                                            fontSize: 'var(--font-size-sm)',
                                            color: 'var(--color-text-secondary)'
                                        }}>
                                            {campaign.funnelStage && (
                                                <span>{campaign.funnelStage}</span>
                                            )}
                                            {campaign.platform && (
                                                <span style={{ textTransform: 'capitalize' }}>
                                                    {campaign.platform}
                                                </span>
                                            )}
                                        </div>

                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-3)',
                                            marginTop: 'var(--space-2)',
                                            fontSize: 'var(--font-size-sm)'
                                        }}>
                                            {campaign.budget && (
                                                <span style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--space-1)'
                                                }}>
                                                    <DollarSign size={14} />
                                                    ${campaign.budget}/{campaign.budgetType === 'lifetime' ? 'total' : 'day'}
                                                </span>
                                            )}
                                            {campaign.startDate && (
                                                <span style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--space-1)'
                                                }}>
                                                    <CalendarIcon size={14} />
                                                    {new Date(campaign.startDate).toLocaleDateString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Edit button */}
                                    <button
                                        className="btn btn-ghost btn-icon"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedCampaignForEdit(campaign);
                                            setEditCampaignModalOpen(true);
                                        }}
                                        style={{ flexShrink: 0 }}
                                    >
                                        <Edit2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Empty state for paid campaigns */}
            {selectedBrand && contentTypeFilter === 'paid' && paidCampaigns.length === 0 && !campaignsLoading && (
                <div className="card" style={{
                    marginTop: 'var(--space-4)',
                    padding: 'var(--space-8)',
                    textAlign: 'center'
                }}>
                    <Megaphone size={48} style={{
                        color: 'var(--color-text-tertiary)',
                        marginBottom: 'var(--space-3)'
                    }} />
                    <h3 style={{ marginBottom: 'var(--space-2)' }}>No Paid Campaigns</h3>
                    <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)' }}>
                        Deploy content from the Bulk Planner or create campaigns in the Funnel Builder.
                    </p>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'center' }}>
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate('/bulk-planner')}
                        >
                            Go to Bulk Planner
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={() => navigate('/funnel-builder')}
                        >
                            <ExternalLink size={16} />
                            Funnel Builder
                        </button>
                    </div>
                </div>
            )}

            {/* Schedule Modal */}
            {showScheduleModal && selectedCreative && (
                <div className="modal-backdrop" onClick={() => setShowScheduleModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                                {/* Thumbnail preview */}
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: 'var(--radius-md)',
                                    overflow: 'hidden',
                                    background: 'var(--color-bg-tertiary)'
                                }}>
                                    {(selectedCreative.url || selectedCreative.videoUrl) ? (
                                        <img
                                            src={selectedCreative.url || selectedCreative.videoUrl}
                                            alt={selectedCreative.title}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    ) : (
                                        <div style={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'var(--color-text-tertiary)'
                                        }}>
                                            {(selectedCreative.title || '?').charAt(0)}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <h2 className="modal-title">Schedule Post</h2>
                                    <p style={{
                                        margin: 0,
                                        fontSize: 'var(--font-size-sm)',
                                        color: 'var(--color-text-secondary)'
                                    }}>
                                        {selectedCreative.title}
                                    </p>
                                </div>
                            </div>
                            <button
                                className="btn btn-icon btn-ghost"
                                onClick={() => setShowScheduleModal(false)}
                            >
                                ×
                            </button>
                        </div>

                        <form onSubmit={handleSchedule}>
                            <div className="modal-body">
                                <div className="input-group">
                                    <label className="input-label">Date*</label>
                                    <input
                                        type="date"
                                        className="input-field"
                                        value={scheduleFormData.date}
                                        onChange={(e) => setScheduleFormData({
                                            ...scheduleFormData,
                                            date: e.target.value
                                        })}
                                        required
                                    />
                                </div>

                                <div className="input-group">
                                    <label className="input-label">Time*</label>
                                    <input
                                        type="time"
                                        className="input-field"
                                        value={scheduleFormData.time}
                                        onChange={(e) => setScheduleFormData({
                                            ...scheduleFormData,
                                            time: e.target.value
                                        })}
                                        required
                                    />
                                </div>

                                <div className="input-group">
                                    <label className="input-label">Timezone</label>
                                    <select
                                        className="select-field"
                                        value={scheduleFormData.timezone}
                                        onChange={(e) => setScheduleFormData({
                                            ...scheduleFormData,
                                            timezone: e.target.value
                                        })}
                                    >
                                        <option value="EST">EST</option>
                                        <option value="PST">PST</option>
                                        <option value="GMT">GMT</option>
                                    </select>
                                </div>

                                <div className="input-group">
                                    <label className="input-label">Platforms*</label>
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 'var(--space-2)'
                                    }}>
                                        {platformOptions.map(platform => (
                                            <label key={platform} className="checkbox-wrapper">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox"
                                                    checked={scheduleFormData.platforms.includes(platform)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setScheduleFormData({
                                                                ...scheduleFormData,
                                                                platforms: [...scheduleFormData.platforms, platform]
                                                            });
                                                        } else {
                                                            setScheduleFormData({
                                                                ...scheduleFormData,
                                                                platforms: scheduleFormData.platforms.filter(p => p !== platform)
                                                            });
                                                        }
                                                    }}
                                                />
                                                <span style={{ textTransform: 'capitalize' }}>{platform}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="input-group">
                                    <label className="input-label">Caption</label>
                                    <textarea
                                        className="input-field"
                                        value={scheduleFormData.caption}
                                        onChange={(e) => setScheduleFormData({
                                            ...scheduleFormData,
                                            caption: e.target.value
                                        })}
                                        placeholder="Write your post caption..."
                                        rows={4}
                                        style={{
                                            resize: 'vertical',
                                            minHeight: '100px',
                                            fontFamily: 'inherit'
                                        }}
                                    />
                                </div>

                                <div className="input-group">
                                    <label className="input-label">Hashtags</label>
                                    <input
                                        type="text"
                                        className="input-field"
                                        value={scheduleFormData.hashtags}
                                        onChange={(e) => setScheduleFormData({
                                            ...scheduleFormData,
                                            hashtags: e.target.value
                                        })}
                                        placeholder="#marketing #socialmedia #brand"
                                    />
                                </div>

                                {/* Paid Content Fields - CTA & Product Page URL */}
                                {(selectedCreative?.contentType === 'paid' || selectedCreative?.metadata?.contentType === 'paid') && (
                                    <div style={{
                                        padding: 'var(--space-3)',
                                        background: 'var(--color-warning-alpha, rgba(245, 158, 11, 0.1))',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--color-warning, #F59E0B)',
                                        marginTop: 'var(--space-3)'
                                    }}>
                                        <div style={{
                                            fontSize: 'var(--font-size-xs)',
                                            fontWeight: 600,
                                            color: 'var(--color-warning, #F59E0B)',
                                            marginBottom: 'var(--space-2)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Paid Campaign Settings
                                        </div>

                                        <div className="input-group" style={{ marginBottom: 'var(--space-3)' }}>
                                            <label className="input-label">Call to Action (CTA)</label>
                                            <input
                                                type="text"
                                                className="input-field"
                                                value={scheduleFormData.cta}
                                                onChange={(e) => setScheduleFormData({
                                                    ...scheduleFormData,
                                                    cta: e.target.value
                                                })}
                                                placeholder="Shop Now, Learn More, etc."
                                            />
                                        </div>

                                        <div className="input-group">
                                            <label className="input-label">Product Page URL</label>
                                            <input
                                                type="url"
                                                className="input-field"
                                                value={scheduleFormData.productPageUrl}
                                                onChange={(e) => setScheduleFormData({
                                                    ...scheduleFormData,
                                                    productPageUrl: e.target.value
                                                })}
                                                placeholder="https://example.com/product"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowScheduleModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={scheduleFormData.platforms.length === 0}
                                >
                                    <CalendarIcon size={16} />
                                    Schedule Post
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Gap Suggestions Modal */}
            <GapSuggestions
                isOpen={showGapSuggestions}
                onClose={() => setShowGapSuggestions(false)}
                targetDate={selectedDate}
                creatives={brandCreatives}
                scheduledPosts={brandScheduledPosts}
                onSchedule={handleScheduleFromSuggestion}
            />

            {/* Post Actions Modal */}
            {showUnscheduleConfirm && creativeToUnschedule && (
                <div className="modal-backdrop" onClick={() => {
                    setShowUnscheduleConfirm(false);
                    setCreativeToUnschedule(null);
                    setPublishError(null);
                }}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
                        <div className="modal-header">
                            <h2 className="modal-title">Scheduled Post</h2>
                            <button
                                className="btn btn-icon btn-ghost"
                                onClick={() => {
                                    setShowUnscheduleConfirm(false);
                                    setCreativeToUnschedule(null);
                                    setPublishError(null);
                                }}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-3)',
                                marginBottom: 'var(--space-4)',
                                padding: 'var(--space-3)',
                                background: 'var(--color-bg-tertiary)',
                                borderRadius: 'var(--radius-md)'
                            }}>
                                {creativeToUnschedule.imageUrl && (
                                    <img
                                        src={creativeToUnschedule.imageUrl}
                                        alt=""
                                        style={{
                                            width: '60px',
                                            height: '60px',
                                            objectFit: 'cover',
                                            borderRadius: 'var(--radius-sm)'
                                        }}
                                    />
                                )}
                                <div>
                                    <p style={{ fontWeight: 600, marginBottom: '4px' }}>
                                        {creativeToUnschedule.title || creativeToUnschedule.creativeTitle}
                                    </p>
                                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: 0 }}>
                                        Scheduled: {new Date(creativeToUnschedule.scheduledDate).toLocaleString()}
                                    </p>
                                    {creativeToUnschedule.platforms && (
                                        <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                                            {creativeToUnschedule.platforms.map(p => (
                                                <span key={p} className="badge badge-info" style={{ textTransform: 'capitalize' }}>
                                                    {p}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {publishError && (
                                <div style={{
                                    padding: 'var(--space-3)',
                                    background: 'var(--color-error-bg)',
                                    border: '1px solid var(--color-error)',
                                    borderRadius: 'var(--radius-md)',
                                    marginBottom: 'var(--space-4)',
                                    color: 'var(--color-error)'
                                }}>
                                    {publishError}
                                </div>
                            )}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => handlePublishNow(creativeToUnschedule)}
                                    disabled={isPublishing}
                                    style={{ justifyContent: 'flex-start' }}
                                >
                                    <Send size={16} />
                                    {isPublishing ? 'Publishing...' : 'Publish Now'}
                                </button>
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleUnschedule}
                                    disabled={isPublishing}
                                    style={{ justifyContent: 'flex-start' }}
                                >
                                    <Trash2 size={16} />
                                    Unschedule
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Scheduled Post Modal (Organic) */}
            <EditScheduledPostModal
                isOpen={editPostModalOpen}
                onClose={() => {
                    setEditPostModalOpen(false);
                    setSelectedPostForEdit(null);
                }}
                post={selectedPostForEdit}
                userId={currentUser?.uid}
                onUpdate={handlePostUpdate}
                onCancel={handleCancelPostFromModal}
                onUnpublish={handleUnpublishPost}
                onRepublish={handleRepublishPost}
                libraryItems={libraryItems}
            />

            {/* Edit Campaign Modal (Paid) */}
            <EditCampaignModal
                isOpen={editCampaignModalOpen}
                onClose={() => {
                    setEditCampaignModalOpen(false);
                    setSelectedCampaignForEdit(null);
                }}
                campaign={selectedCampaignForEdit}
                userId={currentUser?.uid}
                brandId={selectedBrand?.id}
                onUpdate={handleCampaignUpdate}
                onNavigateToFunnel={handleNavigateToFunnel}
                libraryItems={libraryItems}
            />
        </div>
    );
};

export default ContentScheduler;
